import React, { useEffect, useMemo } from "react";
import { Paper, Grid, Typography, Tooltip, Link } from "@mui/material";
import { useParams } from "react-router-dom";
import { round, isNull } from "lodash";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  ORDER_STATUS,
  NOTE_TYPE_VALUE,
  prettyNumericRound,
  getFormattedNumber,
  multiplierMappings,
  BULK_FOOTAGE,
  getFootageValue,
} from "../../../utils";
import { isItemDepartment } from "../../../utils/deptValidator";
import NumericLabel from "../../../components/NumericLabel";
import CustomLabel from "../../../components/CustomLabel";
import ReleaseStatusChip from "../ReleaseStatusChip";
import config from "../../../config";
import { useLazyGetItemPogSummaryQuery } from "../../../services/itemPogSummaryApi";
import { notesApi } from "../../../services/notesApi";

const PaperSx = { p: 1 };

const HeaderSx = { mb: 0.5 };

const CenterAlignSx = { display: "flex", alignItems: "center" };

const ColumnSx = {
  display: "flex",
  flexDirection: "column",
};

const TruncatedSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const LabelSpacingSx = { my: 1 };

const ItemPogSummary = () => {
  const { departmentLetter, pogId } = useParams();
  const store = useSelector((store) => store.global.store);
  const data = useSelector((store) => store.itemPogSummary);
  const [getItemPogSummary] = useLazyGetItemPogSummaryQuery();

  const isPartOfItemsDept = isItemDepartment({
    DepartmentLetter: departmentLetter,
  });

  useEffect(() => {
    getItemPogSummary({
      storeId: store?.id,
      departmentLetter,
      category: null,
      planogramValue: pogId,
      vendorName: null,
      skuNumber: null,
      type: "initialLoad",
    });
  }, [getItemPogSummary, departmentLetter, store?.id, pogId]);

  const { data: plannerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.planner,
      storeId: store.id,
    });

  const { data: retailerNotesData } =
    notesApi.endpoints.getNotesForPlanogram.useQuery({
      noteType: NOTE_TYPE_VALUE.retailer,
      storeId: store.id,
    });

  const plannerNotesValue = useMemo(() => {
    if (data?.allItemsFromFillIn) {
      return "-";
    }
    let notes = plannerNotesData?.find(
      (note) => String(note?.NotesId) === String(data?.plannerNotes)
    );
    notes = !!notes ? notes.Notes : "-";
    return notes;
  }, [plannerNotesData, data?.plannerNotes, data?.allItemsFromFillIn]);

  const retailerNotesValue = useMemo(() => {
    if (data?.allItemsFromFillIn) {
      return "-";
    }
    let notes = retailerNotesData?.find(
      (note) => String(note?.NotesId) === String(data?.retailerNotes)
    );
    notes = !!notes ? notes.Notes : "-";
    return notes;
  }, [retailerNotesData, data?.retailerNotes, data?.allItemsFromFillIn]);

  return (
    <Paper sx={PaperSx}>
      <Grid container spacing={1} alignItems="center" sx={HeaderSx}>
        <Grid item xs={12} md={2.5} sx={CenterAlignSx}>
          <Tooltip title={data?.planogramName} arrow>
            <Typography
              variant="caption"
              fontWeight={500}
              sx={TruncatedSx}
              lineHeight="unset"
            >
              {data?.planogramName}
            </Typography>
          </Tooltip>
          <Typography
            variant="caption"
            fontWeight={500}
            lineHeight="unset"
            mx={0.2}
          >
            -
          </Typography>
          {isPartOfItemsDept ? (
            <Link underline="none" sx={{ fontSize: "12px" }}>
              {data?.planogramNumber}
            </Link>
          ) : (
            <Link
              href={config.PogFileBasePath.concat(data?.planogram)}
              target="_blank"
              rel="noreferrer noopener"
              variant="caption"
              fontWeight={500}
              lineHeight="unset"
            >
              {data?.planogramNumber}
            </Link>
          )}
          {!isPartOfItemsDept && (
            <Typography
              variant="caption"
              fontWeight={500}
              lineHeight="unset"
              ml={0.5}
            >
              {String(data?.lf)?.trim().toLowerCase() ===
              BULK_FOOTAGE.toLowerCase()
                ? `(${BULK_FOOTAGE})`
                : `(${Number(data?.lf) || 0}ft)`}
            </Typography>
          )}
        </Grid>
        <Grid item xs={3} md={2.3}>
          <CustomLabel
            label="Department"
            value={data?.department}
            tooltip={true}
          />
        </Grid>
        <Grid item xs={3} md={2.4}>
          <CustomLabel label="Category" value={data?.category} tooltip={true} />
        </Grid>
        <Grid item xs={3} md={2.4}>
          <CustomLabel
            label="Planner Notes"
            value={plannerNotesValue}
            tooltip={true}
          />
        </Grid>
        <Grid item xs={3} md={2.4}>
          <CustomLabel
            label="Retailer Notes"
            value={retailerNotesValue}
            tooltip={true}
          />
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={0.5}>
        <Grid item xs={3} md={1} sx={ColumnSx}>
          <CustomLabel label="SKUs" value={data?.skus || 0} />
          <CustomLabel
            label="LF"
            value={isPartOfItemsDept ? "-" : getFootageValue(data?.lf)}
            sx={LabelSpacingSx}
          />
          <CustomLabel
            label="Adj. LF"
            value={
              data?.allItemsFromFillIn || isPartOfItemsDept
                ? "-"
                : isNull(data?.adjLf)
                ? getFootageValue(data?.lf)
                : `${data?.adjLf || 0}ft`
            }
          />
        </Grid>
        <Grid item xs={3} md={1.5} sx={ColumnSx}>
          <CustomLabel
            label="Core"
            value={isPartOfItemsDept ? "N" : data?.core}
          />
          <CustomLabel
            label="Region"
            value={data?.region || "-"}
            tooltip={true}
            sx={LabelSpacingSx}
          />
          <CustomLabel
            label="Selected"
            value={data?.allItemsFromFillIn ? "-" : data?.selected || "N"}
          />
        </Grid>
        <Grid item xs={3} md={2} sx={ColumnSx}>
          <CustomLabel
            label="PO Number"
            value={data?.poNumber || "-"}
            tooltip={true}
          />
          <CustomLabel
            label="Status"
            value={
              !!data?.releaseStatus ? (
                <ReleaseStatusChip
                  status={
                    (data?.releaseStatus).toUpperCase() ===
                    ORDER_STATUS.released
                      ? true
                      : false
                  }
                />
              ) : (
                "-"
              )
            }
            sx={!!data?.releaseStatus ? { my: 0.4 } : LabelSpacingSx}
          />
          <CustomLabel
            label="Release Date"
            value={
              data?.allItemsFromFillIn
                ? "-"
                : !!data?.releaseDate
                ? dayjs(data?.releaseDate).format("MM/DD/YYYY")
                : "-"
            }
          />
        </Grid>
        <Grid item xs={3} md={1.5} sx={ColumnSx}>
          <CustomLabel label="Missing" value={data?.missing || 0} />
          <CustomLabel
            label="Qty"
            value={
              isPartOfItemsDept ? (
                "-"
              ) : (
                <NumericLabel isSeparator value={round(data?.qty || 0, 2)} />
              )
            }
            sx={LabelSpacingSx}
          />
          <CustomLabel
            label="Adj. Qty"
            value={
              data?.allItemsFromFillIn ? (
                "-"
              ) : (
                <NumericLabel isSeparator value={round(data?.adjQty || 0, 2)} />
              )
            }
          />
        </Grid>
        <Grid item xs={3} md={2} sx={ColumnSx}>
          <CustomLabel
            label="POG Cost"
            value={`$${getFormattedNumber(
              prettyNumericRound(data?.cost || 0)
            )}`}
          />
          <CustomLabel
            label="Ext. Cost"
            value={
              data?.allItemsFromFillIn
                ? "-"
                : `$${getFormattedNumber(
                    prettyNumericRound(data?.extCost || 0)
                  )}`
            }
            sx={LabelSpacingSx}
          />
          <CustomLabel
            label="Adj. Cost"
            value={
              data?.allItemsFromFillIn
                ? "-"
                : `$${getFormattedNumber(
                    prettyNumericRound(data?.adjCost || 0)
                  )}`
            }
          />
        </Grid>
        <Grid item xs={3} md={2} sx={ColumnSx}>
          <CustomLabel
            label="Core Discount"
            value={
              !isNull(data?.coreDiscount) && !data?.allItemsFromFillIn
                ? `${round(data?.coreDiscount || 0, 2)}%`
                : "-"
            }
          />
          <CustomLabel
            label="Qty Multiplier"
            value={
              !!data?.qtyMultiplier && !data?.allItemsFromFillIn
                ? multiplierMappings[data?.qtyMultiplier]
                : "-"
            }
            sx={LabelSpacingSx}
          />
          <CustomLabel
            label="Discount Multiplier"
            value={
              !!data?.discMultiplier && !data?.allItemsFromFillIn
                ? multiplierMappings[data?.discMultiplier]
                : "-"
            }
          />
        </Grid>
        <Grid item xs={3} md={2} sx={ColumnSx}>
          <CustomLabel
            label="Non-Core Discount"
            value={
              !isNull(data?.nonCoreDiscount) && !data?.allItemsFromFillIn
                ? `${round(data?.nonCoreDiscount || 0, 2)}%`
                : "-"
            }
          />
          <CustomLabel
            label="Dating Days"
            value={
              !isNull(data?.datingDays) && !data?.allItemsFromFillIn
                ? data?.datingDays
                : "-"
            }
            sx={LabelSpacingSx}
          />
          <CustomLabel
            label="Urbanicity"
            value={data?.urbanicity || "-"}
            tooltip={true}
            sx={TruncatedSx}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
export default ItemPogSummary;
